// @flow
import { FormClose } from 'grommet-icons';
import * as React from 'react';
import Attributes from 'esm-web-pro/components/layout/sidePanel/Attributes';
import type { Point11 } from 'esm-web-pro/containers/pages/network/objects/point11/Point11';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import attributeMessages from 'esm-web-pro/messages/attributeMessages';
import type { Match } from 'react-router';
import { withRouter } from 'react-router-dom';
import StyledFormCheckmark from 'esm-web-pro/components/layout/sidePanel/icons/StyledFormCheckmark';
import { connect } from 'react-redux';
import { getCoordinateSystem } from 'esm-web-pro/redux/networks';
import { transformPointCoords } from 'esm-web-pro/containers/pages/network/Util';

type Props = {
    point: Point11,
    intl: IntlShape,
    match: Match,
    onView: Function,
    coordinateSystem: string
}

const Point11Attributes = (props: Props) => {
    const {
        point, intl, match, onView, coordinateSystem,
    } = props;
    const {
        infrastructure_provider: infrastructureProvider, model, underground_connection: undergroundConnection,
        export: eitExport, alias, oss_name: ossName, provider_ref: providerRef, property_owner: propertyOwner,
        project, project_stage: stage, building_status: buildingStatus,
    } = point;

    const attributes = [
        {
            label: intl.formatMessage(attributeMessages.provider),
            value: infrastructureProvider.entity ? infrastructureProvider.entity.name : infrastructureProvider,
        },
        {
            label: intl.formatMessage(attributeMessages.ref),
            value: point.ref,
            onClick: onView,
        },
        {
            label: intl.formatMessage(attributeMessages.coordinates),
            value: coordinateSystem === process.env.REACT_APP_GIS_TRANSFORM_COORDINATE_SYSTEM
                ? transformPointCoords(point.coordinates.coordinates).slice().reverse().join(', ')
                : point.coordinates.coordinates.slice().reverse().join(', '),
        },
    ];
    if (alias) {
        attributes.splice(1, 0, {
            label: intl.formatMessage(attributeMessages.alias),
            value: alias,
        });
    }

    if (providerRef) {
        attributes.push({
            label: intl.formatMessage(attributeMessages.providerRef),
            value: providerRef,
        });
    }

    if (point.type === 'pop') {
        attributes.push(
            {
                label: intl.formatMessage(attributeMessages.ossName),
                value: ossName,
            },
        );
    }

    if (point.type === 'pillar' || point.type === 'shaft' || point.type === 'pop') {
        if (model) {
            attributes.push(...[{
                label: intl.formatMessage(attributeMessages.model),
                value: model,
                // link: model ? `${match.url}model/${model.code}`.replace('//', '/') : '',
            },
            ]);
        }
    }
    if (point.type === 'wallpoint' || point.type === 'cabinet' || point.type === 'pillar' || point.type === 'pop') {
        attributes.push(
            {
                label: intl.formatMessage(attributeMessages.undergroundConnection),
                value: undergroundConnection ? <StyledFormCheckmark /> : <FormClose />,
            },
        );
    }

    if (point.type === 'shaft') {
        const { is_shared: isShared } = point;
        attributes.push(...[
            {
                label: intl.formatMessage(attributeMessages.hasSot),
                value: point.has_sot ? <StyledFormCheckmark /> : <FormClose />,
            },
            {
                label: intl.formatMessage(attributeMessages.shared),
                value: isShared ? <StyledFormCheckmark /> : <FormClose />,
            },
        ]);
    }

    if (propertyOwner) {
        attributes.push({
            label: intl.formatMessage(attributeMessages.propertyOwner),
            value: propertyOwner.name,
            link: `/counterparties/${propertyOwner.uuid}`,
        });
    }

    attributes.push(...[{
        label: intl.formatMessage(attributeMessages.export),
        value: eitExport ? <StyledFormCheckmark /> : <FormClose />,
    },
    ]);


    if (point.type === 'linecut') {
        const { level } = point;
        attributes.push(...[{
            label: intl.formatMessage(attributeMessages.level),
            value: intl.formatMessage(attributeMessages[level]),
        },
        ]);
    }

    if (project) {
        attributes.push({
            label: intl.formatMessage(attributeMessages.project),
            value: project.name,
            link: `/projects/${project.uuid}/dashboard/stages`,
        });
    }
    if (project && stage) {
        attributes.push({
            label: intl.formatMessage(attributeMessages.projectStage),
            value: stage.name,
            link: `/projects/${project.uuid}/dashboard/stages/quick-view/${stage.uuid}`,
        });
    }
    if (buildingStatus) {
        attributes.push({
            label: intl.formatMessage(attributeMessages.buildingStatus),
            value: buildingStatus,
        });
    }
    if (point.regions) {
        attributes.push(
            {
                label: (
                    <FormattedMessage
                        id="map-regions"
                        defaultMessage="Regions"
                    />
                ),
                multiValue: point.regions.map((region) => ({
                    value: region.name,
                    link: region.uuid ? `/infrastructure/map/${point.map_identity}/${match.url.includes('active')
                        ? 'active' : 'draft'}/layer/region/${
                        region.uuid}` : null,
                })),
                separator: ', ',
            },
        );
    }

    return (
        <Attributes attributes={attributes} />
    );
};

const mapStateToProps = (state) => ({ coordinateSystem: getCoordinateSystem(state) });

export default connect(mapStateToProps)(withRouter(injectIntl(Point11Attributes)));
